import { useState, useEffect, useRef } from "react";
import { snsAccounts } from "../../../mocks/marketing";

type Phase = "idle" | "broadcasting" | "live";
type ScoreType = "우수" | "보통" | "미흡";

interface FloatingEmoji {
  id: number;
  emoji: string;
  x: number;
}
interface LiveComment {
  id: number;
  text: string;
  type: "positive" | "negative" | "neutral";
}

const COMMENTS: { text: string; type: "positive" | "negative" | "neutral" }[] = [
  { text: "완전 취향저격 ㅠㅠ 다음 앨범도 기대할게요!", type: "positive" },
  { text: "이 곡 진짜 중독성 미쳤다 ㄹㅇ", type: "positive" },
  { text: "오늘도 스밍 완료! 차트 올라가라 !", type: "positive" },
  { text: "가사가 너무 좋아요, 공감 100%", type: "positive" },
  { text: "뮤비 언제 나오나요?? 기다리다 죽겠음", type: "positive" },
  { text: "팬 아니지만 이건 진짜 좋네요", type: "positive" },
  { text: "처음엔 별로였는데 계속 들으니까 중독됐어요", type: "positive" },
  { text: "홍보 너무 자주 하는 것 같은데..", type: "negative" },
  { text: "이런 스타일이 요즘 유행이구나", type: "neutral" },
  { text: "잘 듣고 갑니다", type: "neutral" },
];
const EMOJIS = ["❤️", "🔥", "💯", "✨", "👏", "😍", "🎵", "💜", "🫶", "🎶"];

const SCORE_CONFIG: Record<ScoreType, { label: string; color: string; bg: string; border: string; badge: string }> = {
  우수: { label: "🔥 바이럴", color: "text-[#E8375F]", bg: "bg-gradient-to-br from-[#E8375F]/8 to-[#FF9F43]/8", border: "border-[#E8375F]/50", badge: "bg-[#E8375F] text-white" },
  보통: { label: "✨ 반응 양호", color: "text-[#FF9F43]", bg: "bg-[#FF9F43]/5", border: "border-[#FF9F43]/30", badge: "bg-[#FF9F43] text-white" },
  미흡: { label: "📊 반응 저조", color: "text-[#8B91A8]", bg: "bg-black/[0.03]", border: "border-[#D5D5D5]", badge: "bg-[#8B91A8] text-white" },
};

export default function SnsPanel() {
  const [activePlatform, setActivePlatform] = useState(snsAccounts[0].id);
  const [postText, setPostText] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [score, setScore] = useState<ScoreType>("보통");
  const [likes, setLikes] = useState(0);
  const [targetLikes, setTargetLikes] = useState(0);
  const [comments, setComments] = useState<LiveComment[]>([]);
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([]);
  const [followerGain, setFollowerGain] = useState(0);
  const [totalFollowerGain, setTotalFollowerGain] = useState(0);
  const [achievements, setAchievements] = useState<string[]>([]);
  const [sessionPosts, setSessionPosts] = useState(0);
  const [sessionLikes, setSessionLikes] = useState(0);
  const [publishedPost, setPublishedPost] = useState<{ text: string; image: string | null } | null>(null);
  const [draftBanner, setDraftBanner] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const emojiIdRef = useRef(0);

  const account = snsAccounts.find((a) => a.id === activePlatform)!;

  /* ── 마케팅 실습 SNS 카피 초안 자동 붙여넣기 ── */
  useEffect(() => {
    try {
      const draft = localStorage.getItem("kap_sns_draft");
      if (draft) {
        localStorage.removeItem("kap_sns_draft");
        setPostText(draft);
        setDraftBanner("마케팅 실습 SNS 카피가 자동으로 붙여넣어졌어요! 수정 후 포스팅하세요.");
        setTimeout(() => setDraftBanner(null), 5000);
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── 보도자료 배포 승인 후 SNS 팔로워 급증 효과 ── */
  useEffect(() => {
    const pending = localStorage.getItem("kap_press_boost_sns");
    if (!pending) return;
    localStorage.removeItem("kap_press_boost_sns");
    const title = localStorage.getItem("kap_press_boost_title") ?? "보도자료";
    // kap_press_boost_title은 ChartPanel에서 이미 지웠을 수도 있으니 무시
    const followerBoost = Math.floor(Math.random() * 1500 + 800);
    const likeBoost = Math.floor(Math.random() * 8000 + 4000);
    setTimeout(() => {
      setTotalFollowerGain(p => p + followerBoost);
      setSessionLikes(p => p + likeBoost);
      setAchievements([
        `📰 언론 보도 효과! 팔로워 +${followerBoost.toLocaleString()}명`,
        `🔥 "${title.slice(0, 14)}" 기사 공유 폭발!`,
      ]);
      setTimeout(() => setAchievements([]), 4500);
    }, 1200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Like counter animation ── */
  useEffect(() => {
    if (likes >= targetLikes) return;
    const remaining = targetLikes - likes;
    const step = Math.max(1, Math.floor(remaining / 18));
    const delay = remaining > 2000 ? 20 : remaining > 500 ? 28 : 40;
    const t = setTimeout(() => setLikes((p) => Math.min(p + step, targetLikes)), delay);
    return () => clearTimeout(t);
  }, [likes, targetLikes]);

  /* ── Floating emoji spawner during live phase ── */
  useEffect(() => {
    if (phase !== "live") return;
    const interval = setInterval(() => {
      if (Math.random() > 0.38) {
        emojiIdRef.current += 1;
        const id = emojiIdRef.current;
        const emoji: FloatingEmoji = {
          id,
          emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
          x: 5 + Math.random() * 90,
        };
        setFloatingEmojis((prev) => [...prev.slice(-10), emoji]);
        setTimeout(() => setFloatingEmojis((prev) => prev.filter((e) => e.id !== id)), 1900);
      }
    }, 380);
    return () => clearInterval(interval);
  }, [phase]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImage(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePost = () => {
    if (!postText.trim()) return;
    const snapshot = { text: postText, image };
    setPublishedPost(snapshot);
    setPhase("broadcasting");
    setPostText("");
    setImage(null);

    setTimeout(() => {
      const len = snapshot.text.length;
      const hasTag = snapshot.text.includes("#");
      const hasImg = !!snapshot.image;
      const s: ScoreType =
        (len > 80 && hasTag) || (hasImg && len > 40) || (len > 100)
          ? "우수"
          : len > 40 || hasTag
          ? "보통"
          : "미흡";
      const target =
        s === "우수"
          ? Math.floor(Math.random() * 6000 + 3000)
          : s === "보통"
          ? Math.floor(Math.random() * 2000 + 400)
          : Math.floor(Math.random() * 250 + 30);
      const gain =
        s === "우수"
          ? Math.floor(Math.random() * 300 + 120)
          : s === "보통"
          ? Math.floor(Math.random() * 80 + 20)
          : Math.floor(Math.random() * 15 + 3);

      setScore(s);
      setTargetLikes(target);
      setLikes(0);
      setFollowerGain(gain);
      setTotalFollowerGain((p) => p + gain);
      setComments([]);
      setSessionPosts((p) => p + 1);
      setSessionLikes((p) => p + target);
      setPhase("live");

      // Reveal comments sequentially
      const pool = [...COMMENTS].sort(() => Math.random() - 0.5).slice(0, 5);
      pool.forEach((c, i) => {
        setTimeout(() => {
          setComments((prev) => [...prev, { ...c, id: i }]);
        }, 600 + i * 550);
      });

      // Achievements
      const ach: string[] = [];
      if (sessionPosts === 0) ach.push("🎉 첫 포스팅 완료!");
      if (s === "우수") ach.push("🔥 바이럴 달성!");
      if (gain >= 200) ach.push("📈 팔로워 폭발!");
      if (sessionPosts + 1 >= 3) ach.push("🏆 오늘의 SNS 마스터!");
      if (ach.length > 0) {
        setAchievements(ach);
        setTimeout(() => setAchievements([]), 3200);
      }
    }, 2100);
  };

  const cfg = SCORE_CONFIG[score];

  return (
    <div className="flex flex-col h-full relative overflow-hidden">

      {/* ── Draft Banner ── */}
      {draftBanner && (
        <div className="absolute top-1 left-0 right-0 z-50 px-1">
          <div className="bg-gradient-to-r from-[#4B6CF7] to-[#7A9BFF] text-white text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-2 animate-slide-in-up">
            <i className="ri-file-copy-line shrink-0"></i>
            <span className="flex-1">{draftBanner}</span>
            <button type="button" onClick={() => setDraftBanner(null)} className="shrink-0 cursor-pointer opacity-70 hover:opacity-100"><i className="ri-close-line"></i></button>
          </div>
        </div>
      )}
      {/* ── Achievement Toasts ── */}
      {achievements.length > 0 && (
        <div className="absolute top-1 left-0 right-0 z-50 flex flex-col gap-1 px-1">
          {achievements.map((a, i) => (
            <div key={i} className="bg-gradient-to-r from-[#E8375F] to-[#FF9F43] text-white text-xs font-bold px-4 py-2 rounded-xl text-center animate-slide-in-up shadow-lg">
              {a}
            </div>
          ))}
        </div>
      )}

      {/* ── Platform Tabs ── */}
      <div className="flex gap-2 mb-3">
        {snsAccounts.map((acc) => (
          <button
            key={acc.id}
            onClick={() => { setActivePlatform(acc.id); setPhase("idle"); setPublishedPost(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
              activePlatform === acc.id
                ? "bg-[#E8375F]/15 text-[#E8375F] border border-[#E8375F]/30"
                : "text-[#8B91A8] border border-[#2A2D3E] hover:bg-[#252830]"
            }`}
          >
            <i className={acc.icon}></i>{acc.platform}
          </button>
        ))}
      </div>

      {/* ── Account Stats Card ── */}
      <div className="bg-[#252830] rounded-xl px-4 py-3 mb-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#111111] text-xs font-semibold">{account.handle}</p>
            <p className="text-[#777777] text-[10px]">{account.platform}</p>
          </div>
          <div className="flex gap-4 text-center">
            <div>
              <div className="text-[#111111] text-sm font-bold tabular-nums">
                {(account.followers + totalFollowerGain).toLocaleString()}
              </div>
              <div className="text-[#777777] text-[10px]">팔로워</div>
            </div>
            <div>
              <div className="text-[#111111] text-sm font-bold tabular-nums">{sessionPosts}</div>
              <div className="text-[#777777] text-[10px]">오늘 포스팅</div>
            </div>
            <div>
              <div className="text-[#E8375F] text-sm font-bold tabular-nums">{sessionLikes.toLocaleString()}</div>
              <div className="text-[#777777] text-[10px]">누적 좋아요</div>
            </div>
          </div>
        </div>
        {totalFollowerGain > 0 && (
          <div className="mt-2 text-[10px] text-[#00C896] font-semibold animate-slide-in-up">
            <i className="ri-arrow-up-line"></i> 총 +{totalFollowerGain}명 팔로워 증가 중
          </div>
        )}
      </div>

      {/* ── PHASE: BROADCASTING ── */}
      {phase === "broadcasting" && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <div className="relative w-20 h-20 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border-2 border-[#E8375F]/40 animate-radio-ring" />
            <div className="absolute inset-0 rounded-full border-2 border-[#E8375F]/25 animate-radio-ring" style={{ animationDelay: "0.45s" }} />
            <div className="absolute inset-0 rounded-full border-2 border-[#E8375F]/15 animate-radio-ring" style={{ animationDelay: "0.9s" }} />
            <div className="w-12 h-12 rounded-full bg-[#E8375F]/10 border border-[#E8375F]/40 flex items-center justify-center">
              <i className="ri-broadcast-line text-[#E8375F] text-xl"></i>
            </div>
          </div>
          <div className="text-center">
            <p className="text-[#111111] font-semibold text-sm animate-shimmer">SNS 전파 중...</p>
            <p className="text-[#777777] text-xs mt-1">AI 알고리즘이 반응을 분석하고 있어요</p>
          </div>
          {publishedPost && (
            <div className="w-full bg-black/[0.04] rounded-xl p-3 border border-black/[0.06]">
              <p className="text-[#555555] text-xs leading-relaxed line-clamp-2">{publishedPost.text}</p>
            </div>
          )}
        </div>
      )}

      {/* ── PHASE: LIVE ── */}
      {phase === "live" && publishedPost && (
        <div className="flex-1 overflow-y-auto">
          {/* Live post card with floating emojis */}
          <div className={`relative rounded-xl p-4 mb-3 border-2 overflow-hidden ${cfg.bg} ${cfg.border}`}>
            {/* Floating emojis layer */}
            <div className="absolute inset-0 pointer-events-none">
              {floatingEmojis.map((fe) => (
                <span
                  key={fe.id}
                  className="absolute text-base animate-float-up select-none"
                  style={{ left: `${fe.x}%`, bottom: "8%" }}
                >
                  {fe.emoji}
                </span>
              ))}
            </div>

            {/* Score badge + time */}
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${cfg.badge}`}>
                {cfg.label}
              </span>
              <span className="text-[#999999] text-[10px]">방금 전</span>
            </div>

            {/* Post content */}
            <p className="text-[#333333] text-xs leading-relaxed mb-3">{publishedPost.text}</p>
            {publishedPost.image && (
              <img
                src={publishedPost.image}
                alt="포스팅 이미지"
                className="w-full h-28 object-cover object-top rounded-lg mb-3"
              />
            )}

            {/* Animated reaction bar */}
            <div className="flex items-center gap-5 relative z-10">
              <div className={`flex items-center gap-1.5 ${likes > 0 ? "animate-like-pop" : ""}`}>
                <i className="ri-heart-3-fill text-[#E8375F] text-base"></i>
                <span className="text-[#E8375F] font-bold text-sm tabular-nums">
                  {likes.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-chat-1-line text-[#777777] text-base"></i>
                <span className="text-[#555555] text-xs tabular-nums">{comments.length}</span>
              </div>
              {score !== "미흡" && (
                <div className="flex items-center gap-1.5">
                  <i className="ri-repeat-line text-[#00C896] text-base"></i>
                  <span className="text-[#00C896] text-xs tabular-nums">
                    {Math.floor(likes / 7)}
                  </span>
                </div>
              )}
              <div className="ml-auto flex items-center gap-1 text-[10px] text-[#00C896] font-semibold">
                <i className="ri-user-add-line"></i>+{followerGain}명
              </div>
            </div>
          </div>

          {/* Comments stream */}
          {comments.length > 0 && (
            <div className="space-y-1.5 mb-3">
              <p className="text-[#999999] text-[10px] font-medium uppercase tracking-wide">
                <i className="ri-robot-line mr-1"></i>AI 댓글 반응
              </p>
              {comments.map((c) => (
                <div
                  key={c.id}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs animate-slide-in-up ${
                    c.type === "positive"
                      ? "bg-[#E8375F]/5 text-[#333333]"
                      : c.type === "negative"
                      ? "bg-black/[0.04] text-[#888888]"
                      : "bg-black/[0.03] text-[#888888]"
                  }`}
                >
                  <i
                    className={`shrink-0 mt-0.5 text-xs ${
                      c.type === "positive"
                        ? "ri-heart-3-fill text-[#E8375F]"
                        : c.type === "negative"
                        ? "ri-thumb-down-line text-[#AAAAAA]"
                        : "ri-chat-1-line text-[#BBBBBB]"
                    }`}
                  />
                  {c.text}
                </div>
              ))}
            </div>
          )}

          {/* Reset button */}
          <button
            onClick={() => setPhase("idle")}
            className="w-full py-2 border border-[#E8375F]/30 text-[#E8375F] text-xs rounded-xl hover:bg-[#E8375F]/5 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-add-line mr-1"></i>새 포스팅 작성하기
          </button>
        </div>
      )}

      {/* ── PHASE: IDLE (compose) ── */}
      {phase === "idle" && (
        <>
          {/* Mini feed */}
          <div className="flex-1 overflow-y-auto space-y-2 mb-3 min-h-0">
            {account.posts.slice(0, 3).map((post) => (
              <div key={post.id} className="bg-[#252830] rounded-xl p-3">
                <p className="text-[#444444] text-xs leading-relaxed mb-2">{post.text}</p>
                {(post as { imageUrl?: string }).imageUrl && (
                  <img
                    src={(post as { imageUrl?: string }).imageUrl}
                    alt="post"
                    className="w-full h-24 object-cover object-top rounded-lg mb-2"
                  />
                )}
                <div className="flex items-center gap-3 text-[10px] text-[#777777]">
                  <span className="text-[#E8375F] flex items-center gap-0.5">
                    <i className="ri-heart-3-line"></i>{post.likes.toLocaleString()}
                  </span>
                  {post.retweets > 0 && (
                    <span className="text-[#00C896] flex items-center gap-0.5">
                      <i className="ri-repeat-line"></i>{post.retweets.toLocaleString()}
                    </span>
                  )}
                  <span className="flex items-center gap-0.5">
                    <i className="ri-chat-1-line"></i>{post.replies.toLocaleString()}
                  </span>
                  <span className="ml-auto">{post.time}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Compose area */}
          <div className="border-t border-black/[0.07] pt-3 shrink-0">
            {/* Tips bar */}
            <div className="flex items-center gap-2 mb-2 text-[10px] text-[#999999]">
              <i className="ri-lightbulb-line text-[#FF9F43]"></i>
              <span>해시태그 + 이미지 첨부 시 반응률 <span className="text-[#00C896] font-semibold">최대 3배↑</span></span>
            </div>

            <textarea
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
              placeholder={`${account.platform}에 올릴 홍보 텍스트... (해시태그 권장)`}
              maxLength={280}
              rows={2}
              className="w-full bg-[#252830] text-xs rounded-lg px-3 py-2 outline-none border border-transparent focus:border-[#E8375F]/40 resize-none placeholder-[#5A5E72]"
              style={{ color: "#111111" }}
            />

            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[#AAAAAA] text-[10px] tabular-nums">{postText.length}/280</span>

              {/* Image upload */}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <button
                onClick={() => fileRef.current?.click()}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] border cursor-pointer whitespace-nowrap transition-all ${
                  image
                    ? "border-[#00C896]/50 text-[#00C896] bg-[#00C896]/5"
                    : "border-black/[0.1] text-[#777777] hover:border-[#E8375F]/40 hover:text-[#E8375F]"
                }`}
              >
                <i className={image ? "ri-image-fill" : "ri-image-add-line"}></i>
                {image ? "이미지 ✓" : "이미지"}
              </button>

              <button
                onClick={handlePost}
                disabled={!postText.trim()}
                className="ml-auto flex items-center gap-1.5 px-4 py-1.5 bg-[#E8375F] hover:bg-[#d42e52] text-white text-xs rounded-lg disabled:opacity-40 cursor-pointer whitespace-nowrap transition-colors font-medium"
              >
                <i className="ri-send-plane-fill"></i>포스팅
              </button>
            </div>

            {/* Image preview */}
            {image && (
              <div className="mt-2 relative w-fit">
                <img src={image} alt="preview" className="h-20 rounded-lg object-cover border border-[#00C896]/30" />
                <button
                  onClick={() => setImage(null)}
                  className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-[#E8375F] rounded-full cursor-pointer"
                >
                  <i className="ri-close-line text-white text-[9px]"></i>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
